import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router';
import { PoliciesClient, PolicyOverview } from '@acorn-portal/shared/src/services/api';
import API from '@acorn-portal/shared/src/services/AxiosInstance';
import { updateFullPolicyDetails } from '@acorn-portal/shared/src/store/fullPolicy.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import API_POLICY_TOKEN from '@acorn-portal/shared/src/services/managedPaymentsInstance';
import { useAuth } from 'react-oidc-context';
import transformFullPolicyToState from '../utils/transform-full-policy-to-state';

export interface IPaging {
	pageNumber: number;
	setPageNumber(p: number): void;
	totalItemCount: number;
	pageSize: number;
}

export interface ILoadingState {
	isLoading: boolean;
	isPaging: boolean;
}

const finishedLoadingState = {
	isLoading: false,
	isPaging: false,
};

export const usePolicyOverviews = (pageSize: number, renewalFilter?: boolean) => {
	const [policies, setPolicies] = useState<PolicyOverview[]>([]);
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [totalPolicyCount, setTotalPolicyCount] = useState<number>(0);
	const [loadingState, setLoadingState] = useState<ILoadingState>({
		isLoading: true,
		isPaging: false,
	});
	const auth = useAuth();
	const isLoadingRef = useRef(false);

	const customerReference = auth.user?.profile?.customerReference;
	const refetchPolicies = async () => {
		if (isLoadingRef.current) {
			return;
		}

		const initialising = !policies.length;

		isLoadingRef.current = true;
		setLoadingState({
			isLoading: initialising,
			isPaging: !initialising,
		});

		try {
			if (customerReference) {
				const client = new PoliciesClient(undefined, API);
				const result = await client.getPolicyOverviews(pageSize * (pageNumber - 1), pageSize, renewalFilter, undefined);

				setPolicies(result.policyOverviews);
				setTotalPolicyCount(result.total);
			}
		} catch (e) {
			console.error(e);
			setPolicies([]);
		} finally {
			isLoadingRef.current = false;
			setLoadingState(finishedLoadingState);
		}
	};

	useEffect(() => {
		refetchPolicies().catch(console.error);
	}, [customerReference, pageNumber, pageSize]);

	return {
		loadingState,
		policies,
		setPolicies,
		refetchPolicies,
		paging: {
			pageNumber,
			setPageNumber: (p: number) => setPageNumber(p),
			totalItemCount: totalPolicyCount,
			pageSize,
		} as IPaging,
	};
};

export const useRenewalOverviews = () => {
	const [pageSize, setPageSize] = useState(50);
	const { loadingState, policies, paging } = usePolicyOverviews(pageSize, true);

	useEffect(() => {
		if (paging.totalItemCount > pageSize) {
			setPageSize(paging.totalItemCount);
		}
	}, [pageSize, paging.totalItemCount]);

	return {
		loadingState: {
			isLoading: loadingState.isLoading,
			isPaging: false,
		},
		policies,
	};
};

export const usePolicy = (policyToken: string | null) => {
	const [loading, setLoading] = useState(false);
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((x) => x.fullPolicy);
	const isLoadingRef = useRef(false);

	useEffect(() => {
		const getPolicyDetails = async () => {
			if (isLoadingRef.current) {
				return;
			}

			if (policyToken) {
				if (fullPolicy.policyToken !== policyToken) {
					isLoadingRef.current = true;
					setLoading(true);
					const client = new PoliciesClient(undefined, API);
					const result = await client.getPolicy(policyToken);
					API_POLICY_TOKEN.defaults.headers.common['X-Token'] = policyToken;
					dispatch(updateFullPolicyDetails(transformFullPolicyToState(result)));
					setLoading(false);
					isLoadingRef.current = false;
				}
			}
		};
		getPolicyDetails().catch(() => {
			setLoading(false);
			isLoadingRef.current = false;
			navigate(ROUTE_PATHS.error);
		});
	}, [policyToken, fullPolicy.policyToken, dispatch, navigate]);

	return { loading, fullPolicy };
};

export default {
	usePolicy,
};
